import { useEffect, useMemo } from 'react';

import { useExternalOrdersContext } from '@context/ExternalOrdersContext';

/**
 * Store selection of the filters modal, held in the module context so it survives a round trip
 * through an order. It is seeded with the whole perimeter and only reseeded when that perimeter
 * itself changes — a store activated or deactivated, a switch to the central kitchen workspace —
 * since a remount must not undo the narrowing the user asked for.
 *
 * While a new perimeter is not acknowledged yet, the selection *is* that perimeter: returning it
 * right away rather than on the next render keeps the list from querying the stores it just lost.
 *
 * @param {Object} params
 * @param {Array<Object>} params.stores - The active stores of the user
 *
 * @returns {Object} The selected stores and their setter
 */
export const usePersistedSelectedStores = ({ stores }) => {
  const { selectedStores, setSelectedStores, storePerimeterKey, setStorePerimeterKey } =
    useExternalOrdersContext();

  const perimeterKey = useMemo(() => JSON.stringify(stores.map(({ id }) => id)), [stores]);

  const hasPerimeterChanged = perimeterKey !== storePerimeterKey;

  useEffect(() => {
    if (!hasPerimeterChanged) {
      return;
    }

    setStorePerimeterKey(perimeterKey);
    setSelectedStores(stores);
  }, [perimeterKey]);

  return {
    selectedStores: hasPerimeterChanged ? stores : selectedStores,
    setSelectedStores,
  };
};
